import { lazy, Suspense, useMemo } from "react";

import { Headset, PhoneOff } from "lucide-react";

import { CallStatus } from "@/components/pieces/roleplays/LiveCallManager";
import { getUser } from "@/components/session/JwtTokenManager";
import { Button } from "@/components/ui/button";
import { FullRoleplaySession } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

const LiveCallManager = lazy(
  () => import("@/components/pieces/roleplays/LiveCallManager"),
);

interface AICoachCallProps {
  roleplaySession: FullRoleplaySession;
  onEndCall: () => void;
  disabled?: boolean;
}

const COACH_ASSISTANT_ID = "60a406f9-41ae-48d2-a151-4caf918c4ff5";

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface CallStateDisplayProps {
  title: string;
  subtitle?: string;
  isPulsing?: boolean;
  duration?: string;
  action?: React.ReactNode;
}

function CallStateDisplay({
  title,
  subtitle,
  isPulsing = false,
  duration,
  action,
}: CallStateDisplayProps) {
  return (
    <div className="flex items-center justify-between h-full px-3 md:px-6">
      <div className="flex items-center gap-2 md:gap-3">
        <div
          className={`w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isPulsing ? "animate-pulse" : ""}`}
        >
          <Headset size={14} color="white" />
        </div>
        <div>
          <div className="text-xs md:text-sm font-semibold text-gray-800">
            {title}
          </div>
          {subtitle && <div className="text-xs text-red-600">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {duration && (
          <div className="text-xs md:text-sm text-gray-500">{duration}</div>
        )}
        {action && action}
      </div>
    </div>
  );
}

function prepareCoachingData(session: FullRoleplaySession) {
  const evaluations = ensureNonNullArray(session.evaluations);

  return {
    scenarioContext: {
      name: session.scenario?.name || "",
      context: session.scenario?.context || session.scenario?.rawContext || "",
      objective:
        session.scenario?.objective || session.scenario?.rawObjective || "",
      persona: session.scenario?.personaName || "",
      personaRole: session.scenario?.personaJobTitle || "",
      difficulty: session.scenario?.difficulty || "",
      minDuration: session.scenario?.minDuration || 90,
    },
    feedbackData: {
      score: session.score,
      rank: session.rank,
      outcomeFeedback: session.outcomeFeedback || "",
      positiveFeedback: session.positiveFeedback || "",
      constructiveFeedback: session.constructiveFeedback || "",
      evaluations: evaluations.map((evaluation) => ({
        criterionName: evaluation.evaluationCriterionItem?.name || "",
        criterionGroup:
          evaluation.evaluationCriterionItem?.evaluationCriterion?.name || "",
        grade: evaluation.grade || "",
        feedback: evaluation.feedback || "",
        summary: evaluation.summary || "",
        examples: ensureNonNullArray(evaluation.feedbackExamples).map(
          (example) => ({
            feedback: example.feedback || "",
            grade: example.grade || "",
            quote: example.quote || "",
            suggestionQuote: example.suggestionQuote || "",
          }),
        ),
      })),
    },
  };
}

export default function AICoachCall({
  roleplaySession,
  onEndCall,
}: AICoachCallProps) {
  const user = getUser();

  const variableValues = useMemo(() => {
    const coachingData = prepareCoachingData(roleplaySession);
    return {
      userName: user?.name || "there",
      scenarioContext: JSON.stringify(coachingData.scenarioContext),
      feedbackData: JSON.stringify(coachingData.feedbackData),
    };
  }, [roleplaySession, user]);

  const handleSaveCall = () => {
    // AI Coach calls don't need to be saved
    onEndCall();
  };

  return (
    <Suspense
      fallback={
        <CallStateDisplay
          title="Loading AI Coach..."
          isPulsing={true}
          action={
            <Button size="sm" className="opacity-0 pointer-events-none">
              Placeholder
            </Button>
          }
        />
      }
    >
      <LiveCallManager
        assistantId={COACH_ASSISTANT_ID}
        handleSaveCall={handleSaveCall}
        disabled={false}
        skipCountdown={true}
        variableValues={variableValues}
      >
        {({
          callState,
          callDuration,
          onStartCall,
          onEndCall: endCall,
          onCancelCall,
        }) => {
          const isIdle = callState.status === CallStatus.IDLE;
          const isConnecting = callState.status === CallStatus.CONNECTING;
          const isConnected = callState.status === CallStatus.CONNECTED;
          const isEnding = callState.status === CallStatus.ENDING;
          const hasError = callState.status === CallStatus.ERROR;

          // Auto-start the call when component mounts
          if (isIdle) {
            onStartCall();
          }

          // Determine the action buttons based on state
          let actionButtons = null;
          if (isConnecting) {
            actionButtons = (
              <Button
                size="sm"
                intent="secondary"
                onClick={() => {
                  onCancelCall();
                  onEndCall();
                }}
              >
                Cancel
              </Button>
            );
          } else if (isConnected) {
            actionButtons = (
              <Button size="sm" onClick={endCall}>
                <PhoneOff strokeWidth={1} fill="white" size={14} />
                End Call
              </Button>
            );
          } else if (hasError) {
            actionButtons = (
              <Button size="sm" intent="secondary" onClick={onEndCall}>
                Close
              </Button>
            );
          }

          // Determine the display props based on state
          if (isIdle) {
            return (
              <CallStateDisplay
                title="Initializing..."
                isPulsing={true}
                action={
                  <Button size="sm" className="opacity-0 pointer-events-none">
                    Placeholder
                  </Button>
                }
              />
            );
          }

          if (isConnecting) {
            return (
              <CallStateDisplay
                title="Connecting to AI Coach..."
                isPulsing={true}
                action={actionButtons}
              />
            );
          }

          if (isConnected) {
            return (
              <CallStateDisplay
                title="AI Coach Session Active"
                duration={formatDuration(callDuration)}
                action={actionButtons}
              />
            );
          }

          if (isEnding) {
            return (
              <CallStateDisplay
                title="Ending session..."
                action={
                  <Button size="sm" className="opacity-0 pointer-events-none">
                    Placeholder
                  </Button>
                }
              />
            );
          }

          if (hasError) {
            return (
              <CallStateDisplay
                title="Connection Error"
                subtitle={
                  callState.error || "Failed to connect. Please try again."
                }
                action={actionButtons}
              />
            );
          }

          // Fallback (shouldn't happen)
          return <CallStateDisplay title="Unknown state" />;
        }}
      </LiveCallManager>
    </Suspense>
  );
}
